import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppAuth } from '../hooks/useAppAuth';
import { createApiClient, walletService, platformFeeConfigService } from '../services/api';
import {
  WalletTopUpMethod, WalletTopUpStatus, WalletEntryType,
  type CreateWalletTopUpResponse,
} from '../types';

const ENTRY_LABELS: Record<WalletEntryType, string> = {
  [WalletEntryType.TopUp]: 'Top-up',
  [WalletEntryType.Payment]: 'Payment',
  [WalletEntryType.Receipt]: 'Received',
  [WalletEntryType.Fee]: 'Fee',
  [WalletEntryType.Refund]: 'Refund',
  [WalletEntryType.Withdrawal]: 'Withdrawal',
  [WalletEntryType.Adjustment]: 'Adjustment',
};

const STATUS_LABELS: Record<WalletTopUpStatus, string> = {
  [WalletTopUpStatus.Pending]: 'Awaiting card payment',
  [WalletTopUpStatus.AwaitingApproval]: 'Awaiting confirmation',
  [WalletTopUpStatus.Completed]: 'Completed',
  [WalletTopUpStatus.Rejected]: 'Rejected',
  [WalletTopUpStatus.Failed]: 'Failed',
};

export default function WalletPage() {
  const { getToken } = useAppAuth();
  const qc = useQueryClient();
  const api = () => walletService(createApiClient(getToken));

  const [amount, setAmount] = useState('50');
  const [method, setMethod] = useState<WalletTopUpMethod>(WalletTopUpMethod.Stripe);
  const [instruction, setInstruction] = useState<CreateWalletTopUpResponse | null>(null);

  const { data: wallet, isLoading } = useQuery({
    queryKey: ['wallet'],
    queryFn: () => api().get(),
  });

  const { data: statement = [] } = useQuery({
    queryKey: ['wallet-statement'],
    queryFn: () => api().getStatement(),
  });

  const { data: topUps = [] } = useQuery({
    queryKey: ['wallet-topups'],
    queryFn: () => api().getMyTopUps(),
  });

  const { data: fees } = useQuery({
    queryKey: ['platform-fee-effective'],
    queryFn: () => platformFeeConfigService(createApiClient(getToken)).getEffective(),
  });

  const parsedAmount = Number(amount) || 0;
  const cardFeeRate  = fees?.walletTopUpFeePercent ?? 0.02;
  // The fee is added on top, so the wallet always receives exactly what was asked for.
  const previewFee    = method === WalletTopUpMethod.Stripe ? Math.round(parsedAmount * cardFeeRate * 100) / 100 : 0;
  const previewCharge = parsedAmount + previewFee;

  const topUp = useMutation({
    mutationFn: () => api().requestTopUp(parsedAmount, method),
    onSuccess: (result) => {
      setInstruction(result);
      qc.invalidateQueries({ queryKey: ['wallet'] });
      qc.invalidateQueries({ queryKey: ['wallet-topups'] });
    },
    onError: (e: any) => alert(e?.response?.data?.error ?? 'Could not start the top-up.'),
  });

  return (
    <div className="page-shell-tight" data-testid="wallet-page">
      <h1 className="text-xl font-bold text-gray-900 mb-1">My Wallet</h1>
      <p className="text-sm text-gray-500 mb-6">
        Spend your balance on Bazaar purchases and job payments. Paying another TransitDeal
        member from your wallet is settled inside the platform, so it carries no platform charge.
      </p>

      {/* ── Balance ─────────────────────────────────────────────────────────── */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 mb-6 shadow-sm">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Available balance</p>
        {isLoading ? (
          <p className="text-gray-400">Loading…</p>
        ) : (
          <p className="text-3xl font-bold text-gray-900" data-testid="wallet-balance">
            £{(wallet?.balance ?? 0).toFixed(2)}
          </p>
        )}
        {wallet?.isFrozen && (
          <p className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
             data-testid="wallet-frozen-notice">
            This wallet is frozen and cannot be used. Contact support.
          </p>
        )}
      </div>

      {/* ── Top up ──────────────────────────────────────────────────────────── */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 mb-6 shadow-sm" data-testid="topup-panel">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Add funds</h2>

        <label className="text-xs font-medium text-gray-600 block mb-1">Amount to add (£)</label>
        <input
          type="number" min="1" step="0.01" value={amount}
          onChange={e => setAmount(e.target.value)}
          data-testid="topup-amount-input"
          className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 mb-3"
        />

        <div className="grid grid-cols-2 gap-3 mb-4">
          <MethodCard
            testId="method-card"
            selected={method === WalletTopUpMethod.Stripe}
            onSelect={() => setMethod(WalletTopUpMethod.Stripe)}
            title="Card"
            subtitle={`${(cardFeeRate * 100).toFixed(0)}% fee added on top`}
          />
          <MethodCard
            testId="method-bank"
            selected={method === WalletTopUpMethod.BankTransfer}
            onSelect={() => setMethod(WalletTopUpMethod.BankTransfer)}
            title="Bank transfer"
            subtitle="Free — confirmed by our team"
          />
        </div>

        <div className="text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2 mb-4">
          <Row label="Added to wallet" value={`£${parsedAmount.toFixed(2)}`} testId="preview-credit" />
          <Row label="Fee" value={`£${previewFee.toFixed(2)}`} testId="preview-fee" />
          <Row label="You pay" value={`£${previewCharge.toFixed(2)}`} testId="preview-charge" bold />
        </div>

        <button
          onClick={() => topUp.mutate()}
          disabled={parsedAmount <= 0 || topUp.isPending || wallet?.isFrozen}
          data-testid="submit-topup"
          className="text-sm font-semibold px-4 py-2 rounded-lg bg-blue-600 text-white disabled:opacity-50"
        >
          {topUp.isPending ? 'Starting…' : 'Add funds'}
        </button>

        {instruction && instruction.method === WalletTopUpMethod.BankTransfer && (
          <div className="mt-4 text-sm border border-blue-200 bg-blue-50 rounded-lg p-3" data-testid="bank-instruction">
            <p className="font-semibold text-blue-900 mb-1">Send £{instruction.amount.toFixed(2)} by bank transfer</p>
            <p className="text-blue-900">
              Quote reference <span className="font-mono font-semibold" data-testid="bank-reference">{instruction.bankReference}</span>.
              Your balance updates once our team confirms the funds have arrived.
            </p>
          </div>
        )}

        {instruction && instruction.method === WalletTopUpMethod.Stripe && instruction.clientSecret && (
          <div className="mt-4 text-sm border border-blue-200 bg-blue-50 rounded-lg p-3" data-testid="card-instruction">
            <p className="text-blue-900">
              Complete the card payment of £{instruction.chargedAmount.toFixed(2)} to add
              £{instruction.amount.toFixed(2)} to your wallet.
            </p>
          </div>
        )}
      </div>

      {/* ── Top-up history ──────────────────────────────────────────────────── */}
      {topUps.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-5 mb-6 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Top-ups</h2>
          <div className="flex flex-col gap-2" data-testid="topup-history">
            {topUps.map(t => (
              <div key={t.id} data-testid={`topup-row-${t.id}`}
                   className="flex items-center justify-between text-sm border-b border-gray-100 pb-2 last:border-0">
                <div>
                  <p className="font-medium text-gray-900">£{t.amount.toFixed(2)}</p>
                  <p className="text-xs text-gray-500">
                    {t.method === WalletTopUpMethod.Stripe ? 'Card' : 'Bank transfer'}
                    {t.feeAmount > 0 && ` · fee £${t.feeAmount.toFixed(2)} · paid £${t.chargedAmount.toFixed(2)}`}
                    {t.bankReference && ` · ${t.bankReference}`}
                  </p>
                  {t.rejectionReason && <p className="text-xs text-red-600">{t.rejectionReason}</p>}
                </div>
                <span data-testid="topup-status"
                      className={`text-xs font-semibold px-2 py-0.5 rounded ${
                        t.status === WalletTopUpStatus.Completed ? 'bg-green-100 text-green-800'
                        : t.status === WalletTopUpStatus.Rejected || t.status === WalletTopUpStatus.Failed
                          ? 'bg-red-100 text-red-800'
                          : 'bg-amber-100 text-amber-800'}`}>
                  {STATUS_LABELS[t.status]}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Statement ───────────────────────────────────────────────────────── */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Statement</h2>
        {statement.length === 0 ? (
          <p className="text-sm text-gray-400" data-testid="empty-statement">No wallet activity yet.</p>
        ) : (
          <div className="flex flex-col gap-2" data-testid="wallet-statement">
            {statement.map(e => (
              <div key={e.id} data-testid="statement-entry"
                   className="flex items-center justify-between text-sm border-b border-gray-100 pb-2 last:border-0">
                <div>
                  <p className="font-medium text-gray-900">{e.description ?? ENTRY_LABELS[e.entryType]}</p>
                  <p className="text-xs text-gray-500">{new Date(e.createdAt).toLocaleString('en-GB')}</p>
                </div>
                <div className="text-right">
                  <p className={`font-semibold ${e.amount < 0 ? 'text-red-600' : 'text-green-700'}`}
                     data-testid="entry-amount">
                    {e.amount < 0 ? '−' : '+'}£{Math.abs(e.amount).toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-400">£{e.balanceAfter.toFixed(2)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MethodCard({
  selected, onSelect, title, subtitle, testId,
}: { selected: boolean; onSelect: () => void; title: string; subtitle: string; testId: string }) {
  return (
    <button
      onClick={onSelect}
      data-testid={testId}
      aria-pressed={selected}
      className={`text-left rounded-lg border px-3 py-3 ${
        selected ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-white'}`}
    >
      <p className="text-sm font-semibold text-gray-900">{title}</p>
      <p className="text-xs text-gray-500">{subtitle}</p>
    </button>
  );
}

function Row({ label, value, bold, testId }: { label: string; value: string; bold?: boolean; testId: string }) {
  return (
    <div className={`flex justify-between ${bold ? 'font-semibold text-gray-900 pt-1 border-t border-gray-200 mt-1' : 'text-gray-600'}`}>
      <span>{label}</span>
      <span data-testid={testId}>{value}</span>
    </div>
  );
}